import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, X, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';

export default function ReasoningPopover({ open, onClose, assignmentId, anchorRect }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open || !assignmentId) return;
    setLoading(true);
    (async () => {
      try {
        const { data: r } = await api.get(`/ai/explain-cell/${assignmentId}`);
        setData(r);
      } catch (e) {
        console.warn('[reasoning] uitleg laden mislukt', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, assignmentId]);

  async function sendFeedback(feedbackType, detail = '') {
    if (!data?.assignment) return;
    try {
      await api.post('/ai/feedback', {
        employee_id: data.assignment.employee_id,
        location_id: data.assignment.location_id,
        feedback_type: feedbackType,
        detail,
        source_assignment_id: assignmentId,
      });
      setSent(true);
      setTimeout(() => onClose(), 1200);
    } catch (e) {
      console.warn('[reasoning] feedback verzenden mislukt', e);
      toast({ title: 'Feedback niet verzonden', description: 'Probeer het zo nog eens.', variant: 'destructive' });
    }
  }

  if (!open) return null;

  // Position near the anchor
  const style = anchorRect ? {
    top: Math.min(window.innerHeight - 360, anchorRect.bottom + 8),
    left: Math.min(window.innerWidth - 420, Math.max(8, anchorRect.left - 100)),
  } : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="fixed inset-0 z-30" onClick={onClose}>
      <div
        className="fixed w-[400px] rounded-xl bg-white shadow-2xl border border-slate-200 p-4"
        style={style}
        onClick={(e) => e.stopPropagation()}
        data-testid="reasoning-popover"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-brand-amber" />
            <p className="text-sm font-semibold text-slate-900">AI redenering</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-slate-100"><X size={14} /></button>
        </div>

        {loading ? (
          <p className="text-xs text-slate-500">Laden…</p>
        ) : !data?.reasoning ? (
          <p className="text-xs text-slate-500" data-testid="reasoning-empty">{data?.message || 'Geen AI-uitleg beschikbaar'}</p>
        ) : sent ? (
          <p className="text-sm text-green-700 text-center py-4">Feedback verstuurd, dank je!</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-800 leading-snug" data-testid="reasoning-text">{data.reasoning.reasoning_text}</p>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500">Vertrouwen:</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-brand-amber" style={{ width: `${(data.reasoning.confidence * 100).toFixed(0)}%` }} />
              </div>
              <span className="font-semibold text-slate-700 tabular-nums">{(data.reasoning.confidence * 100).toFixed(0)}%</span>
            </div>
            {data.reasoning.factors?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1.5">Factoren:</p>
                <div className="space-y-1">
                  {data.reasoning.factors.slice(0, 5).map((f, i) => (
                    <div key={f.name || `factor-${i}`} className="flex items-center justify-between text-xs">
                      <span className="text-slate-700">{f.name}</span>
                      <div className="flex items-center gap-1">
                        <div className="h-1 w-20 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-brand-navy" style={{ width: `${((f.weight || 0) * 100).toFixed(0)}%` }} />
                        </div>
                        <span className="tabular-nums text-slate-500">{((f.weight || 0) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-1">Feedback?</p>
              <div className="flex gap-1">
                <button onClick={() => sendFeedback('good')} data-testid="feedback-good"
                  className="flex-1 flex items-center justify-center gap-1 rounded-md border border-green-200 px-2 py-1 text-xs text-green-700 hover:bg-green-50">
                  <ThumbsUp size={11} /> Goed
                </button>
                <button onClick={() => sendFeedback('wrong_person')} data-testid="feedback-bad"
                  className="flex-1 flex items-center justify-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50">
                  <ThumbsDown size={11} /> Niet oké
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 text-right">
              {data.reasoning.model_version}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
